import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Table, Input, Tag, Select, Button } from 'antd';
import { SearchOutlined, SendOutlined } from '@ant-design/icons';
import axiosInstance from '../../variables/api/axiosInstance';
import { formatDateHeure } from '../../variables/constants';
import TableExportButton from '../../components/TableExportButton';

import { avecLibelleTraduit } from '../../i18n/libelles';

const { Search } = Input;

const CATEGORIES = avecLibelleTraduit([
  { value: 'GARANTIE' },
  { value: 'RESPONSABILITE' },
  { value: 'EXPERTISE' },
  { value: 'OFFRE' },
  { value: 'PAIEMENT' },
  { value: 'RECOURS' },
  { value: 'PV' },
  { value: 'PRESCRIPTION' },
  { value: 'INTERNE' },
], 'relances:categories');

const STATUT_ENVOI = avecLibelleTraduit({
  ENVOYE: { color: 'green' },
  ECHEC: { color: 'red' },
  EN_ATTENTE: { color: 'orange' },
}, 'relances:historique.statutsEnvoi');

export default function HistoriqueRelancesTab() {
  const { t } = useTranslation('relances');
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [searchText, setSearchText] = useState('');
  const [filtreCategorie, setFiltreCategorie] = useState(null);
  const PAGE_SIZE = 30;

  /**
   * `GET /v1/messagerie/relances` ne prend AUCUN parametre : il renvoie la liste
   * complete des relances de l'utilisateur. Les `page` / `size` / `search` /
   * `categorie` qui etaient envoyes ici partaient dans le vide - la zone de
   * recherche et le selecteur de categorie ne filtraient rien, en silence.
   *
   * On charge donc une fois, et on filtre/pagine cote client sur la liste obtenue.
   * Le jour ou le backend acceptera des criteres, il suffira de les repasser en
   * parametres et de retirer le filtrage local.
   */
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axiosInstance.get('/v1/messagerie/relances');
      const d = r.data?.data || r.data || {};
      setItems(Array.isArray(d) ? d : (d.content || []));
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const itemsFiltres = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    return items.filter((it) => {
      if (filtreCategorie && it.categorieDelai !== filtreCategorie) return false;
      if (!q) return true;
      return [it.sinistreNumeroLocal, it.sujet, it.destinataire, it.expediteurNom]
        .filter(Boolean)
        .some((champ) => String(champ).toLowerCase().includes(q));
    });
  }, [items, searchText, filtreCategorie]);

  // Le total suit ce qui est reellement affiche : afficher le total brut a cote
  // d'une liste filtree ferait croire a une pagination cassee.
  useEffect(() => { setTotal(itemsFiltres.length); }, [itemsFiltres]);

  const handleSearch = (v) => {
    setSearchText(v);
    setPage(0);
  };

  const handleCatChange = (v) => {
    setFiltreCategorie(v);
    setPage(0);
  };

  const columns = [
    {
      title: t('historique.colonnes.dateEnvoi'),
      dataIndex: 'dateEnvoi',
      width: 150,
      render: (v) => <span style={{ fontSize: 12.5, color: '#374151' }}>{formatDateHeure(v)}</span>,
      format: 'datetime',
    },
    {
      title: t('historique.colonnes.sinistre'),
      dataIndex: 'sinistreNumeroLocal',
      width: 150,
      render: (v, r) => r.sinistreTrackingId ? (
        <Button
          type="link"
          size="small"
          style={{ padding: 0, fontSize: 12.5, fontFamily: 'ui-monospace,monospace', color: '#1a6b3c' }}
          onClick={() => navigate(`/sinistres/${r.sinistreTrackingId}`)}
        >
          {v || r.sinistreTrackingId}
        </Button>
      ) : <span style={{ fontFamily: 'ui-monospace,monospace', fontSize: 12.5 }}>{v || '-'}</span>,
      export: (v) => v ?? '',
    },
    {
      title: t('historique.colonnes.sujet'),
      dataIndex: 'sujet',
      ellipsis: true,
      render: (v) => <span style={{ fontSize: 12.5 }}>{v}</span>,
    },
    {
      title: t('historique.colonnes.destinataire'),
      dataIndex: 'destinataire',
      width: 200,
      ellipsis: true,
      render: (v) => <span style={{ fontSize: 12, color: '#6b7280' }}>{v || '-'}</span>,
    },
    {
      title: t('historique.colonnes.categorieDelai'),
      dataIndex: 'categorieDelai',
      width: 140,
      render: (v) => v ? (
        <Tag style={{ borderRadius: 10, fontSize: 11 }}>
          {CATEGORIES.find(c => c.value === v)?.label || v}
        </Tag>
      ) : '-',
      export: (v) => CATEGORIES.find(c => c.value === v)?.label ?? (v || ''),
    },
    {
      title: t('historique.colonnes.envoi'),
      dataIndex: 'statutEnvoi',
      width: 110,
      render: (v) => {
        const s = STATUT_ENVOI[v] || { label: v || '-', color: 'default' };
        return <Tag color={s.color} style={{ borderRadius: 10, fontSize: 11 }}>{s.label}</Tag>;
      },
      export: (v) => STATUT_ENVOI[v]?.label ?? (v || ''),
    },
    {
      title: t('historique.colonnes.envoyePar'),
      dataIndex: 'expediteurNom',
      width: 140,
      render: (v) => <span style={{ fontSize: 12, color: '#6b7280' }}>{v || '-'}</span>,
    },
  ];

  return (
    <div style={{ padding: '12px 0' }}>
      {/* Barre filtres */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
        <Search
          placeholder={t('historique.recherche')}
          allowClear
          enterButton={<SearchOutlined />}
          onSearch={handleSearch}
          style={{ width: 280 }}
          size="middle"
        />
        <Select
          placeholder={t('historique.colonnes.categorieDelai')}
          allowClear
          onChange={handleCatChange}
          style={{ width: 180 }}
          options={CATEGORIES}
          size="middle"
        />
      </div>

      <TableExportButton
        floating
        data={itemsFiltres}
        columns={columns}
        filename="historique-relances"
        title={t('historique.exportTitre')}
        subtitle={t('historique.exportSousTitre', { count: total })}
        count={total}
      />

      <Table
        rowKey={(r) => r.trackingId || r.id || `${r.dateEnvoi}-${r.sinistreNumeroLocal}`}
        loading={loading}
        dataSource={itemsFiltres}
        columns={columns}
        size="middle"
        pagination={{
          current: page + 1,
          pageSize: PAGE_SIZE,
          total,
          onChange: (p) => setPage(p - 1),
          showTotal: (n) => t('historique.total', { count: n }),
          showSizeChanger: false,
        }}
        locale={{
          emptyText: (
            <div style={{ padding: '32px 0', textAlign: 'center', color: '#9ca3af' }}>
              <SendOutlined style={{ fontSize: 28, marginBottom: 8, display: 'block' }} />
              {t('historique.vide')}
            </div>
          ),
        }}
      />
    </div>
  );
}
